// react and mui stuff
import { useContext, useEffect, useState } from "react"

import Button from "@mui/joy/Button"
import Modal from "@mui/joy/Modal"
import ModalClose from "@mui/joy/ModalClose"
import Sheet from "@mui/joy/Sheet"
import TabList from "@mui/joy/TabList"
import TabPanel from "@mui/joy/TabPanel"
import Tabs from "@mui/joy/Tabs"
import Tab from "@mui/joy/Tab"
import Typography from "@mui/joy/Typography"
import Link from "@mui/joy/Link"
import Tooltip from "@mui/joy/Tooltip"

import { ToastContext } from "../components/toast"
import { UserContext } from "../utils/contexts"

// custom components
import { GB } from "../components/emoji"
import DetailCard from "../components/detailcard"

// data and stuff
import { Game, BoughtGame, GameList } from "../types/game"
import { CliUser } from "../types/users"
import games from "../data/placeholdergames"
import { setData } from "../utils/setdata"
import { updateUser } from "../utils/utils"
import { imgSrc, gameAPI } from "../utils/backend"

function ViewGameButton({ game, loading }: { game: Game; loading: boolean; }) {
  const link = game.steamId ? `https://store.steampowered.com/app/${game.steamId}` : `https://store.steampowered.com/search/?term=${game.title.replace(/ /g, "+")}`;

  return <Button
    aria-label={`View ${game} on the Steam store page.`}
    onClick={() => {if (!loading) window.open(link, "_blank")}}
    startDecorator={<img src={imgSrc("logos/steam.svg")} width="24px" height="16.06px" alt=""/>}
  >
    View Game
  </Button>
}

function GameImage({ game, i }: { game: Game, i: number }) {
  const src = game.steamId ? `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${game.steamId}/header.jpg` : imgSrc("placeholder/game.jpg")
  return <img src={src} alt="" height="auto" width="100%" style={{ aspectRatio: "16 / 9", objectFit: "contain" }} loading={ i > 5 ? "lazy" : "eager"}/>
}

type DisplayProps = {
  games: (Game | BoughtGame)[]
  user: CliUser
  loading: boolean
  setPurchase: Function
  setToast: Function
}

function GameDisplay({ games, user, loading, setPurchase, setToast }: DisplayProps) {

  function copyKey(code: string = "") {
    // advanced stuff
    navigator.clipboard.writeText(code);
    setToast({ text: `Copied the game key!`, color: "success" });
  }


  const gb = user.balances?.gb ?? 0

  return (
    <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", justifyContent: "center" }}>
      {
        games.map((g, i) => {
          // too expensive
          const noBuy = gb < g.cost;
          const buttons = [];

          // view game button
          if (g.system.toLowerCase() === "steam") buttons.push(<ViewGameButton game={g} loading={loading} key="view"/>);
          
          let details: any[];

          if ("key" in g) {
            buttons.push(<Button key="copy" color="success" onClick={() => copyKey(g.key ?? "")}>Copy Game Key</Button>)
            details = [["Rating", g.rating], ["Purchased", g.date]];
          } else {
            // ooh shiny buy button
            const buyButton = <Button key="buy"
              sx={{ height: "100%"}}
              loading={loading}
              disabled={noBuy}
              color="success"
              aria-label={`Purchase ${g.title} for ${g.cost} Ghostbucks`}
              onClick={() => {
                if (!loading) setPurchase(g)
              }}
            >
              Purchase
            </Button>

            // ooh dull too expensive button
            if (noBuy) buttons.push(<Tooltip title="You don't have enough GB!" placement="top"><span>{buyButton}</span></Tooltip>)
            else buttons.push(buyButton)

            const cost =
            <div style={{display: "flex", alignContent: "center"}}>
              <GB size={24}/>
              {g.cost}
            </div>
            details = [["Cost", cost], ["Rating", g.rating]]
          }

          return (
            <DetailCard
              loading={loading}
              name={g.title}
              subtitle={g.system}
              details={details}
              img={<GameImage game={g} i={i}/>}
              buttons={buttons}
              key={g.code}
            />
          )
        })
      }
    </div>
  )
}

export default function Games() {
  const [gameList, setGameList] = useState<GameList>(games())
  const [purchase, setPurchase] = useState<Game>();
  const [loading, setLoading] = useState(true)
  const { user, setUser } = useContext(UserContext)
  const { setToast } = useContext(ToastContext)

  useEffect(() => {
    // get the data
    if (!user || gameList.available[0].title !== "Terra - The Card Game") return;
    gameAPI.list().then(info => {
      if (!setData(info, setToast, user, setUser)) return;

      // success!
      setUser(updateUser(user, info.user));
      setGameList(info.games);

      setLoading(false)
    })
  // eslint-disable-next-line
  }, [setToast, setUser, user])

  if (!user) return (<div/>)

  function buyGame(code: string = "NULL") {
    // yay we're buying a game :D
    setLoading(true);

    gameAPI.purchase(code).then(res => {
      const onFinish = () => {
        setPurchase(undefined);
        setLoading(false);
      }
      if (!setData(res, setToast, user, setUser, onFinish) || !user) return;

      setUser(updateUser(user, res.user));
      setGameList(res.games);

      // visual feedback
      setToast({ text: `Bought ${purchase?.title}! Check the Purchased tab to get the key.`, color: "success"});
      onFinish();
    })
  }

  const gb = user.balances?.gb ?? 0

  return (
    <div className="contentRoot" style={{ width: "100%" }}>
      {/** Purchase Modal */}
      <Modal
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
        open={Boolean(purchase)}
        onClose={() => setPurchase(undefined)}
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Sheet variant="outlined" sx={{ maxWidth: 500, borderRadius: 'md', p: 3, boxShadow: 'lg' }}>
          <ModalClose variant="plain" sx={{ m: 1 }} />
          <Typography component="h2" id="modal-title" level="h4" textColor="inherit" sx={{ fontWeight: 'lg', mb: 1 }}>
            Are you sure?
          </Typography>
          <Typography id="modal-desc" textColor="text.tertiary" component="span">
            {purchase?.title} costs <GB size={24} middle/><b>{purchase?.cost}</b>.<br/>This will leave you with <GB size={24} middle/><b>{`${gb - (purchase?.cost ?? 0)}`}</b> in your account.
          </Typography>
          <br/>
          <Button loading={loading} onClick={() => buyGame(purchase?.code)}>Purchase</Button>
          <Button loading={loading} color="warning" variant="outlined" sx={{marginLeft: "15px"}} onClick={() => setPurchase(undefined)}>Cancel</Button>
        </Sheet>
      </Modal>

      <h1 className="pageTitle">LDSG Game Store</h1>

      {/** Purchased and Available Panels */}
      <Tabs defaultValue={0} sx={{width: "100%"}}>
        <Sheet sx={{ position: "sticky", top: -16, zIndex: 10, padding: "10px", paddingBottom: "0px" }}>
          <br/>
          <div className="flexMidVert">
            <GB size={30}/>
            <Typography level="h3">Balance: {gb}</Typography>
          </div>
          <br/>
          <TabList>
              <Tab>Available</Tab>
              <Tab>Purchased</Tab>
          </TabList>
        </Sheet>
          {/** Available */}
          <TabPanel value={0} sx={{width: "100%"}}>
            <GameDisplay games={gameList.available} loading={loading} setPurchase={setPurchase} setToast={setToast} user={user}/>
          </TabPanel>

          {/** Purchased */}
          <TabPanel value={1} sx={{width: "100%"}}>
            <Link href="https://help.steampowered.com/en/faqs/view/2A12-9D79-C3D7-F870" target="_blank" rel="noreferrer">Learn how to redeem games</Link>
            {gameList.purchased.length === 0 && <Typography><br/>Looks like you haven't purchased any games!</Typography>}
            <GameDisplay games={gameList.purchased} loading={loading} setPurchase={setPurchase} setToast={setToast} user={user}/>
          </TabPanel>
      </Tabs>
    </div>
  )
}